import {Bridge, BridgeMessage, SleepAsync, Timer} from "js-vextensions";
import {GenerateUUID} from "mobx-graphlink";
import {RunInAction} from "web-vcore";
import {ShowMessageBox} from "react-vmessagebox";
import {JustBeforeUI_listeners} from "../../Main.js";
import {ProcessMessage} from "../../Server/DB/@Shared/@ProcessMessage.js";
import {MeID} from "../../Server/DB/users.js";
import {store} from "../../Store/index.js";
import {gql} from "@apollo/client";
import {wsClient_onConnectedListeners, wsClient_onDisconnectedListeners} from "../LibIntegrations/Apollo.js";
import {Float16ArrayToBase64} from "../General/Numbers.js";
import {InAndroid, nativeBridge} from "./Bridge_Native.js";
import {GetLiveLocalSession} from "../../Engine/LiveSessionManagement.js";
import {NarrateText_ForSession, NarrateText_StopAll, NarrateText_lastUtteranceId} from "../Services/TTS.js";
import type {VRComp} from "../../Engine/LiveSession/Components/VRComp.js";
import type {ConceptLinkComp} from "../../Engine/LiveSession/Components/ConceptLinkComp.js";
import {GetCache, GetImageFilesWithSubpath, GetVideoFilesWithSubpath} from "../../Store/main/general/CacheState.js";
import {ResizeBase64Image} from "../UI/CanvasUtils.js";
import {GetSelectedEngineConfig} from "../../Server/DB/engineConfigs.js";
import {FrameCharacter, ImageFrameFlawType} from "../../Server/DB/engineConfigs/@EngineConfig/@EC_VR.js";
import {AssertNotify} from "web-vcore";
import {WeightedRandom} from "../General/General.js";
import {GDBelt_RawDataChunk, NormalizeChunkToInMemoryShape, gdBelt_rawDataDB} from "../IndexedDB/GDBelt_RawDataDB.js";
import {InitRTCServer, rtcServer} from "./Bridge_WebRTC/RTCServer.js";
import {ImageFileEntry, VideoFileEntry} from "./Bridge_Native/MediaTypes.js";
import type {MemoryPalaceComp, MPPromptInfo} from "../../Engine/LiveSession/Components/MemoryPalaceComp.js";
import {MemoryPalace_RecallResult} from "../../Engine/LiveSession/SessionEvent.js";

// Note: A "remote user process" is just any remote instance (so a remote site, desktop, or android instance -- though atm we only actually use it to send messages between site instances)

export const processId = `${Date.now()}_${GenerateUUID()}`;
export const remoteUserProcessesBridge = new Bridge({
	receiveChannelMessageFunc_addImmediately: false,
	receiveChannelMessageFunc_adder: receiveChannelMessageFunc=>{
		const apollo = store.graphlink.subs.apollo;
		const apolloObservable = apollo.subscribe({
			query: gql`
				subscription($input: NewProcessMessagesInput!) {
					newProcessMessages(input: $input) {
						newMessage {
							userChannel
							senderProcessId
							sendTime
							message
						}
					}
				}
			`,
			variables: {input: {userChannel: MeID()!}},
		});
		const subscription = apolloObservable.subscribe({
			next: result=>{
				if (result.error) console.error("Subscription error:", result.error);
				if (result.data == null) return;
				const message = (result.data as unknown as {newProcessMessages: {newMessage: ProcessMessage}}).newProcessMessages.newMessage;
				//ProcessUserProcessMessage(message);
				//receiveChannelMessageFunc(message.message);
				MaybeReceiveProcessMessageAsBridgeChannelMessage(message, receiveChannelMessageFunc);
			},
		});
		wsClient_onDisconnectedListeners.push(()=>subscription.unsubscribe());
		//wsClient_onConnectedListeners.push(()=>subscription.unsubscribe()); // also unsubscribe on connect happening (to avoid duplicate subscriptions, for if SetUpReceiver is called prior to connection)
	},
	sendChannelMessageFunc: channelMessage=>{
		// based on our settings (no-wrap, and no-stringify), we know that the channelMessage argument will be a BridgeMessage structure; so narrow its type to that
		const bridgeMessage = channelMessage as BridgeMessage;
		MaybeSendBridgeMessageAsProcessMessage(bridgeMessage);
	},
	channel_wrapBridgeMessage: false,
	channel_stringifyChannelMessageObj: false,
	channel_safeCallbacks: true,
	requireMainFuncForCalls: false, // this bridge may have >2 instances connected at once, so just ignore calls to functions that are missing (some are only attached when eg. session is started)
});
/*JustBeforeUI_listeners.push(()=>{
	const waitTimer = new Timer(1000, ()=>{
		if (MeID() == null) return;
		waitTimer.Stop();
		remoteUserProcessesBridge.SetUpReceiver();
	}).Start();
});*/

// whenever the client connects/reconnects, (re-)subscribe to the process-messages
// TODO: Make this also occur when the user signs-in. (workaround for now is to refresh the page)
wsClient_onConnectedListeners.push(()=>{
	// only set up receiver if we are signed-in (else it would just error anyway)
	if (MeID()) {
		remoteUserProcessesBridge.SetUpReceiver();
		console.log("Set up remoteUserProcessesBridge receiver.");
	} else {
		console.log("Not setting up remoteUserProcessesBridge receiver, since not signed in.");
	}
});

export function MaybeReceiveProcessMessageAsBridgeChannelMessage(message: ProcessMessage, receiveChannelMessageFunc: (channelMessage: string | object)=>any) {
	if (message.userChannel != MeID()) return; // defensive
	if (message.senderProcessId == processId) return;
	// based on our settings (no-wrap, and no-stringify), we know that the message.message will be a BridgeMessage structure; so narrow its type to that
	const bridgeMessage = message.message as BridgeMessage;

	const vrSettings = store.main.settings.vr;
	// if receiving callback (ie. getting response of an allowed effect-send -- as allowed through sendRemoteEffects), let this always happen
	const isReceivingCallback = bridgeMessage.callCallback_id != null && vrSettings.sendRemoteEffects;
	if (!isReceivingCallback && !vrSettings.receiveRemoteEffects) {
		console.log(`Receiving remote effects are disabled for this instance (see engine->config->host), so ignoring process-message:`, message);
		return;
	}

	receiveChannelMessageFunc(message.message);
}
/*function ProcessUserProcessMessage(message: ProcessMessage) {
	[...]
	console.log(`Processing process-message:`, message);
	const msg = message.message;
	if (msg.requestTTS) {
		const liveSession = GetLiveLocalSession();
		if (liveSession) {
			const {text, volumeMultiplier} = msg.requestTTS;
			NarrateText_ForSession(liveSession, text, volumeMultiplier ?? 1);
		}
	}
}*/

export function MaybeSendBridgeMessageAsProcessMessage(bridgeMessage: BridgeMessage) {
	const vrSettings = store.main.settings.vr;
	// if calling callback (ie. responding to an allowed effect-receive -- as allowed through receiveRemoteEffects), let this always happen
	const isCallingCallback = bridgeMessage.callCallback_id != null && vrSettings.receiveRemoteEffects;
	if (!isCallingCallback && !vrSettings.sendRemoteEffects) {
		console.log(`Sending remote effects are disabled for this instance (see engine->config->client), so ignoring send of bridge-message:`, bridgeMessage);
		return;
	}

	const apollo = store.graphlink.subs.apollo;
	const processMessage = new ProcessMessage({
		userChannel: MeID()!,
		senderProcessId: processId,
		message: bridgeMessage,
	});
	(async()=>{
		const fetchResult = await apollo.mutate({
			mutation: gql`
				mutation($input: ProcessMessageInput!) {
					sendProcessMessage(input: $input) {
						__typename
					}
				}
			`,
			variables: {input: {message: processMessage}},
		});
		const _returnData = (fetchResult.data as unknown as {sendProcessMessage: unknown}).sendProcessMessage;
	})();
}

// for VR component (mostly at least)
// ==========

remoteUserProcessesBridge.RegisterFunction("RequestTTS", async(text: string, volumeMultiplier_preGlobalMultiplier?: number, speedMultiplier?: number)=>{
	const liveSession = GetLiveLocalSession();
	if (liveSession) {
		await NarrateText_ForSession({session: liveSession, volumeMultiplier_preGlobalMultiplier: volumeMultiplier_preGlobalMultiplier ?? 1}, {text, speedMultiplier: speedMultiplier ?? 1});
		//await RunCLVoicing(followUpWithCLVoicing_config);
	}
});
export function Remote_RequestTTS(text: string, volumeMultiplier_preGlobalMultiplier?: number, speedMultiplier?: number) {
	remoteUserProcessesBridge.Call_NoReturn("RequestTTS", text, volumeMultiplier_preGlobalMultiplier, speedMultiplier);
}

remoteUserProcessesBridge.RegisterFunction("RequestPlayAudioClipForFrame", async(frame_poolName: string, frame_texturePath: string, playClipFromAnotherEpisode: boolean)=>{
	const liveVRComp = GetLiveLocalSession()?.Comp_ByString("VRComp") as VRComp|n;
	if (liveVRComp) {
		await liveVRComp.PlayAudioClipForFrame(frame_poolName, frame_texturePath, playClipFromAnotherEpisode);
		//await RunCLVoicing(followUpWithCLVoicing_config);
	}
});
export function Remote_RequestPlayAudioClipForFrame(frame_poolName: string, frame_texturePath: string, playClipFromAnotherEpisode: boolean) {
	remoteUserProcessesBridge.Call_NoReturn("RequestPlayAudioClipForFrame", frame_poolName, frame_texturePath, playClipFromAnotherEpisode);
}

// video works a bit differently; there are a small enough number of videos, that we just send the whole list to the client, and then the client can just pick one itself
remoteUserProcessesBridge.RegisterFunction("GetVideoFileEntries", async()=>{
	return GetVideoFilesWithSubpath("");
});
export async function Remote_GetVideoFileEntries() {
	return await remoteUserProcessesBridge.Call("GetVideoFileEntries") as ImageFileEntry[];
}

type Remote_CLVoicingConfig = {delay: number, volumeMultiplier: number, utteranceId?: string|n};
async function RunCLVoicing(config: Remote_CLVoicingConfig|n) {
	if (config != null) {
		await SleepAsync(config.delay);
		const conceptLinkComp = GetLiveLocalSession()?.Comp_ByString("ConceptLinkComp") as ConceptLinkComp|n;
		if (conceptLinkComp) conceptLinkComp.VoiceNextSequence(config.volumeMultiplier, config.utteranceId != null ? {utteranceId: config.utteranceId} : undefined);
	}
}
remoteUserProcessesBridge.RegisterFunction("RunCLVoicing", async(config: Remote_CLVoicingConfig|n)=>{
	return await RunCLVoicing(config);
});
export async function Remote_RunCLVoicing(config: Remote_CLVoicingConfig|n) {
	return await remoteUserProcessesBridge.Call("RunCLVoicing", config);
}

remoteUserProcessesBridge.RegisterFunction("StopTTSIfLastUtteranceIdIsX", async(utteranceId: string)=>{
	/*const conceptLinkComp = GetLiveLocalSession()?.Comp_ByString("ConceptLinkComp") as ConceptLinkComp|n;
	if (conceptLinkComp) conceptLinkComp.CancelLastSequenceNarrate();*/
	if (NarrateText_lastUtteranceId == utteranceId) {
		NarrateText_StopAll();
		return true;
	}
	return false;
});
/** Returns true if the last utterance-id matched. (and thus also, whether NarrateText_StopAll was called) */
export async function Remote_StopTTSIfLastUtteranceIdIsX(utteranceId: string) {
	return await remoteUserProcessesBridge.Call("StopTTSIfLastUtteranceIdIsX", utteranceId);
}

// todo: probably merge this with RunCLVoicing above
remoteUserProcessesBridge.RegisterFunction("ConceptLinkVoiceNextSequence", async(volumeMultiplier: number)=>{
	const conceptLinkComp = GetLiveLocalSession()?.Comp_ByString("ConceptLinkComp") as ConceptLinkComp|n;
	if (conceptLinkComp) conceptLinkComp.VoiceNextSequence(volumeMultiplier);
});
export async function Remote_ConceptLinkVoiceNextSequence(volumeMultiplier: number) {
	return await remoteUserProcessesBridge.Call("ConceptLinkVoiceNextSequence", volumeMultiplier);
}

remoteUserProcessesBridge.RegisterFunction("MemoryPalaceNewPrompt", async()=>{
	const memoryPalaceComp = GetLiveLocalSession()?.Comp_ByString("MemoryPalaceComp") as MemoryPalaceComp|n;
	if (memoryPalaceComp) return await memoryPalaceComp.NewPrompt();
});
export async function Remote_MemoryPalaceNewPrompt() {
	return await remoteUserProcessesBridge.Call("MemoryPalaceNewPrompt") as MPPromptInfo|n;
}

remoteUserProcessesBridge.RegisterFunction("MemoryPalaceNotifyRecallResult", (data: MemoryPalace_RecallResult)=>{
	const memoryPalaceComp = GetLiveLocalSession()?.Comp_ByString("MemoryPalaceComp") as MemoryPalaceComp|n;
	if (memoryPalaceComp && data) memoryPalaceComp.NotifyRecallResult(data);
});
export function Remote_MemoryPalaceNotifyRecallResult(data: MemoryPalace_RecallResult) {
	remoteUserProcessesBridge.Call("MemoryPalaceNotifyRecallResult", data);
}

remoteUserProcessesBridge.RegisterFunction("NotifyVRHeadsetState", (onHead: boolean)=>{
	const liveVRComp = GetLiveLocalSession()?.Comp_ByString("VRComp") as VRComp|n;
	if (liveVRComp) {
		liveVRComp.NotifyVRHeadsetState(onHead);
	}
});
export function Remote_NotifyVRHeadsetState(onHead: boolean) {
	remoteUserProcessesBridge.Call("NotifyVRHeadsetState", onHead);
}

export class NewFrameInfo {
	constructor(data: NewFrameInfo) { Object.assign(this, data); }
	poolName!: string;
	targetInitials: string|n;
	imagePath: string|n;
	imageStr: string|n;
	videoPath: string|n;
	videoDuration: number|n; // in ms
}
remoteUserProcessesBridge.RegisterFunction("GetInfoForNewFrame", async(reencode_size: number|n, reencode_quality: number, poolToSelect?: string|n, poolToExclude?: string|n)=>{
	const config = GetSelectedEngineConfig.CatchBail(null);
	if (!config) return null;
	const enabledPools = config.vr.frames_pools.filter(a=>a.enabled && a.name != poolToExclude);
	const selectedPool = poolToSelect ? enabledPools.find(a=>a.name == poolToSelect) : WeightedRandom(enabledPools, a=>a.weight);
	if (!selectedPool) return null;
	const mediaPathFilter = selectedPool.mediaPathFilter.toLowerCase();
	const mediaPathExclude = selectedPool.mediaPathExclude.toLowerCase();

	// image selection
	// ==========

	const imageFolder = config.vr.frames_imageFolder;
	const imageFiles = GetImageFilesWithSubpath(imageFolder).filter(file=>{
		if (mediaPathFilter && !file.filePath.toLowerCase().includes(mediaPathFilter)) return false;
		if (mediaPathExclude && file.filePath.toLowerCase().includes(mediaPathExclude)) return false;
		return true;
	});

	let selectedChar: FrameCharacter|n;
	let imageFile: ImageFileEntry|n;
	let imageAsBase64: string|n;
	if (imageFiles.length > 0) {
		if (selectedPool.flaw_type == ImageFrameFlawType.wrongNameVoiced) {
			const chars = selectedPool.characters;
			if (chars.length == 0) return null;
			// select a random character from the list, based on their "weight" value
			selectedChar = WeightedRandom(chars, a=>a.weight ?? 0);

			const imageFilesWithCharInitials = imageFiles.filter(a=>a.filePathInfo.characterInitials.includes(selectedChar!.initials));
			imageFile = imageFilesWithCharInitials[Math.floor(Math.random() * imageFilesWithCharInitials.length)];
		} else {
			imageFile = imageFiles[Math.floor(Math.random() * imageFiles.length)] as ImageFileEntry|n;
		}

		const frameData_useImage = !selectedPool.videoClip_enabled;
		if (frameData_useImage && imageFile) {
			imageAsBase64 = (await nativeBridge.Call("GetImageAsBase64", imageFile.contentUri)) as string;

			const needReencodeForResize = reencode_size && Math.max(imageFile.width, imageFile.height) > reencode_size;
			const needReencodeForQuality = reencode_quality != null && reencode_quality < 1;
			if (needReencodeForResize || needReencodeForQuality) {
				const scaleToApply = needReencodeForResize ? reencode_size / Math.max(imageFile.width, imageFile.height) : 1;
				const newImageAsBase64 = await ResizeBase64Image(imageAsBase64, scaleToApply, reencode_quality);
				// only proceed with the resize/reencoding if it actually reduced the byte-length of the image
				// (otherwise it's just counterproductive, since the only point of the resizing/reencoding is to reduce the image transfer-size)
				if (newImageAsBase64.length < imageAsBase64.length) {
					imageAsBase64 = newImageAsBase64;
				}
			}
		}
	}

	// video selection
	// ==========

	let videoFile: VideoFileEntry|n;
	if (selectedPool.videoClip_enabled) {
		const videoFolder = config.vr.frames_videoFolder;
		const videoFiles = GetVideoFilesWithSubpath(videoFolder).filter(file=>{
			if (mediaPathFilter && !file.filePath.toLowerCase().includes(mediaPathFilter)) return false;
			if (mediaPathExclude && file.filePath.toLowerCase().includes(mediaPathExclude)) return false;
			return true;
		});
		if (videoFiles.length == 0) {
			AssertNotify(false, `No video-files found in folder "${videoFolder}" for remotely-requested frame-streaming frame. @mediaPathFilter(${mediaPathFilter}) @mediaPathExclude(${mediaPathExclude})`);
			return null;
		}

		// maybe temp: first, try to get the video-file that matches the image-file path-info (if any)
		const imageFilePathInfo = imageFile?.filePathInfo;
		if (imageFilePathInfo) {
			videoFile = videoFiles.find(a=>{
				const videoFilePathInfo = a.filePathInfo;
				return (videoFilePathInfo.seriesStr == imageFilePathInfo.seriesStr &&
					videoFilePathInfo.seasonStr == imageFilePathInfo.seasonStr &&
					videoFilePathInfo.episodeStr == imageFilePathInfo.episodeStr);
			});
			if (videoFile == null) console.warn(`No video-file found that matches the image-file's path-info (${imageFilePathInfo.seriesStr}, ${imageFilePathInfo.seasonStr}, ${imageFilePathInfo.episodeStr}), so selecting a random video-file instead.`);
		}

		// if video-file not resolved from the above, select a random video-file from the list
		if (videoFile == null) {
			videoFile = videoFiles[Math.floor(Math.random() * videoFiles.length)];
		}

		// note: the video-file's contents get requested later (in ImageFrame.AcquireFrameDataAndResolveMetadata)
	}

	return new NewFrameInfo({
		poolName: selectedPool.name,
		targetInitials: selectedChar?.initials,
		imagePath: imageFile?.filePath,
		imageStr: imageAsBase64,
		videoPath: videoFile?.filePath,
		videoDuration: videoFile?.duration,
	});
});
export async function Remote_GetInfoForNewFrame(reencode_size: number|n, reencode_quality: number, timeoutForResend?: number, poolToSelect?: string|n, poolToExclude?: string|n): Promise<NewFrameInfo|n> {
	const attemptRemoteCall = ()=>remoteUserProcessesBridge.Call("GetInfoForNewFrame", reencode_size, reencode_quality, poolToSelect, poolToExclude) as Promise<NewFrameInfo|n>;

	// If no timeout is specified, just make a single call
	if (!timeoutForResend) {
		return await attemptRemoteCall();
	}

	return new Promise((resolve, reject)=>{
		let resolved = false; // resolved or rejected
		let requestCount = 0;
		const maxRequests = 5; // Safety limit to prevent infinite requests

		const individualErrors = [] as any[];
		const reject_combinedMessage = ()=>{
			if (requestCount >= maxRequests) {
				resolved = true;
				reject(new Error(`Max request limit reached for Remote_GetInfoForNewFrame after ${maxRequests} requests failed to resolve, each with a timeout of ${timeoutForResend}ms. Individual request errors:\n${individualErrors.map(a=>a.message).join("\n")}`));
			}
		};

		const attemptRemoteCall_withHandling = ()=>{
			requestCount++;
			(async()=>{
				try {
					const result = await attemptRemoteCall();
					if (!resolved) {
						resolved = true;
						resolve(result as NewFrameInfo|n);
					}
				} catch (error) {
					/*if (!resolved && requestCount >= maxRequests) {
						resolved = true;
						reject(error);
					}*/
					individualErrors.push(error);
				}
			})();
		};

		// Make the initial request
		attemptRemoteCall_withHandling();

		// Set up a timer to make new requests until we get a response
		const timer = new Timer(timeoutForResend, ()=>{
			if (resolved) {
				timer.Stop();
				return;
			}
			if (requestCount >= maxRequests) {
				timer.Stop();
				reject_combinedMessage();
				return;
			}

			console.warn(`Request for Remote_GetInfoForNewFrame timed out after ${timeoutForResend}ms, sending retry #${requestCount}...`);
			attemptRemoteCall_withHandling();
		}).Start();
	});
}

remoteUserProcessesBridge.RegisterFunction("NotifyUserChangedActiveFrame", async(firstDisplay: boolean)=>{
	const vrComp = GetLiveLocalSession()?.Comp_ByString("VRComp") as VRComp|n;
	if (vrComp) return vrComp.NotifyUserChangedActiveFrame(firstDisplay);
	return {newCount: -1, newTimeCredit: -1};
});
export async function Remote_NotifyUserChangedActiveFrame(firstDisplay: boolean) {
	return (await remoteUserProcessesBridge.Call("NotifyUserChangedActiveFrame", firstDisplay)) as {
		newCount: number, /** in ms */ newTimeCredit: number, newMPPCount: number
	};
}

remoteUserProcessesBridge.RegisterFunction("NotifyUserMarkedImageFrameAsLie", async(userWasCorrect: boolean, imagePath: string|n)=>{
	const vrComp = GetLiveLocalSession()?.Comp_ByString("VRComp") as VRComp|n;
	if (vrComp) vrComp.NotifyUserMarkedImageFrameAsLie(userWasCorrect, imagePath);
});
export async function Remote_NotifyUserMarkedImageFrameAsLie(userWasCorrect: boolean, imagePath: string|n) {
	return await remoteUserProcessesBridge.Call("NotifyUserMarkedImageFrameAsLie", userWasCorrect, imagePath);
}

remoteUserProcessesBridge.RegisterFunction("NotifyUserMarkedImageFrameAsMistagged", async(imagePath: string|n)=>{
	const vrComp = GetLiveLocalSession()?.Comp_ByString("VRComp") as VRComp|n;
	if (vrComp) vrComp.NotifyUserMarkedImageFrameAsMistagged(imagePath);
});
export async function Remote_NotifyUserMarkedImageFrameAsMistagged(imagePath: string|n) {
	return await remoteUserProcessesBridge.Call("NotifyUserMarkedImageFrameAsMistagged", imagePath);
}

// others
// ==========

// The wire-shape for breath-chunk transfer: only `samples_force_f16_base64` (base64 of the raw little-endian
// f16 bytes) is carried on the wire, plus the small per-chunk metadata. This avoids (a) the 43% bloat of
// letting JSON.stringify serialize Float16Array as object-form {"0":...,"1":...}, and (b) the much larger
// (~7x) ASCII-array form. Legacy (pre-migration) chunks persisting `samples_force: number[]` get quantized
// to f16 on-the-fly during the encode step below, so the wire shape is uniform regardless of source format.
type BreathChunk_WireDTO = {
	sessionIdPlusStartTime: string;
	sessionId: string;
	startTime: number;
	samples_force_f16_base64: string;
};

remoteUserProcessesBridge.RegisterFunction("RequestSessionBreathData", async(sessionID: string, callback: (chunks: BreathChunk_WireDTO[])=>any)=>{
	const chunks = await gdBelt_rawDataDB.GetChunksForSession(sessionID);
	// No data available, so await forever ("hack" fix for problem of other site-instances responding
	// earlier with null, and thus the response from the one with data being ignored/lost).
	if (chunks.length == 0) {
		//return null;
		await new Promise(()=>{}); // await forever
	}

	// Convert each normalized in-memory chunk (samples_force: number[]) into a wire-DTO that carries only
	// the f16-base64 sample buffer. This works uniformly for both new (f16-persisted) and legacy (number[])
	// chunks, since GetChunksForSession already normalized both into samples_force: number[].
	const dtos = chunks.map(chunk=>({
		sessionIdPlusStartTime: chunk.sessionIdPlusStartTime,
		sessionId: chunk.sessionId,
		startTime: chunk.startTime,
		samples_force_f16_base64: Float16ArrayToBase64(Float16Array.from(chunk.samples_force)),
	} as BreathChunk_WireDTO));
	callback(dtos);
});
export async function RequestSessionBreathData(sessionID: string, callback: (chunks: GDBelt_RawDataChunk[])=>any) {
	// On receive: normalize the wire-DTOs at this layer (before the user-supplied callback runs) so that
	// callers see the same normalized in-memory shape (samples_force: number[]) they would get from a
	// direct local GetChunksForSession call. This DRYs the format-awareness through NormalizeChunkToInMemoryShape.
	const userCallback_normalized = (dtoChunks: BreathChunk_WireDTO[])=>{
		const normalizedChunks = dtoChunks.map(dto=>NormalizeChunkToInMemoryShape(dto as GDBelt_RawDataChunk));
		callback(normalizedChunks);
	};
	return await remoteUserProcessesBridge.Call("RequestSessionBreathData", sessionID, userCallback_normalized);
}


// TEMP (just ensure RTCServer.ts runs)
if (InAndroid(0)) {
	InitRTCServer();
}

// remote cursor time-sync
// ==========

export type RemoteCursorState = {entryId: string|n; segmentId: string|n; cursorPos: number};

/** True on this instance when the user has agreed to broadcast cursor movements (lasts until page refresh). */
let requestCursorSendingDialog_lastShownTime = 0;
let requestCursorSendingDialog_open = false;
remoteUserProcessesBridge.RegisterFunction("RequestCursorSendingStart", ()=>{
	const now = Date.now();
	if (requestCursorSendingDialog_open) return; // already waiting for a response
	if (now - requestCursorSendingDialog_lastShownTime < 5_000) return; // shown too recently

	requestCursorSendingDialog_open = true;
	requestCursorSendingDialog_lastShownTime = now;

	ShowMessageBox({
		title: "Start cursor broadcasting?",
		cancelButton: true,
		message: `
			A remote LF page/instance is requesting that this instance broadcast its cursor position changes.
			Start broadcasting? (will become active until page refresh)
		`.AsMultiline(0),
		onOK: ()=>{
			RunInAction("cursorSendingActive", ()=>store.main.settings.transcribe.cursorSendingActive = true);
			requestCursorSendingDialog_open = false;
		},
		onCancel: ()=>{
			requestCursorSendingDialog_open = false;
		},
	});
});
export function Remote_RequestCursorSendingStart() {
	remoteUserProcessesBridge.Call_NoReturn("RequestCursorSendingStart");
}

remoteUserProcessesBridge.RegisterFunction("NotifyCursorMove", (entryId: string, segmentId: string, timestamp: number|null)=>{
	if (!store.main.settings.transcribe.timeSyncActive) return;
	RunInAction("NotifyCursorMove", ()=>store.main.settings.transcribe.remoteCursorState = {entryId, segmentId, timestamp});
});
export function Remote_NotifyCursorMove(entryId: string, segmentId: string, timestamp: number|null) {
	if (!store.main.settings.transcribe.cursorSendingActive) return;
	remoteUserProcessesBridge.Call_NoReturn("NotifyCursorMove", entryId, segmentId, timestamp);
}

remoteUserProcessesBridge.RegisterFunction("RequestAudioPlayPause", (play: boolean)=>{
	const transcribeState = store.main.settings.transcribe;
	if (!transcribeState.timeSyncActive) return;
	RunInAction("RequestAudioPlayPause", ()=>{
		const prev = transcribeState.remotePlayPause;
		transcribeState.remotePlayPause = {play, id: (prev?.id ?? 0) + 1};
	});
});
export function Remote_RequestAudioPlayPause(play: boolean) {
	if (!store.main.settings.transcribe.cursorSendingActive) return;
	remoteUserProcessesBridge.Call_NoReturn("RequestAudioPlayPause", play);
}

remoteUserProcessesBridge.RegisterFunction("RequestAudioSpeed", (rate: number)=>{
	const transcribeState = store.main.settings.transcribe;
	if (!transcribeState.timeSyncActive) return;
	RunInAction("RequestAudioSpeed", ()=>transcribeState.audioPlaybackRate = rate);
});
export function Remote_RequestAudioSpeed(rate: number) {
	if (!store.main.settings.transcribe.cursorSendingActive) return;
	remoteUserProcessesBridge.Call_NoReturn("RequestAudioSpeed", rate);
}